// DTO de `GET /api/supervisor/pendientes` (S10 — pendientes de validar).
//
// Lista TODOS los bloques de producción sin validar (`validado_at IS NULL`,
// `estado = 'valido'`) del scope del supervisor en un rango de fechas, con
// filtros opcionales por turno y línea. Cada bloque marca si RETIENE la
// facturación de su OP (regla DN4: una OP terminada — cierre manual o capturada
// ≥ programada — con ≥ 1 bloque sin validar NO entra al corte de facturación).
//
// Scope gobernado por `usuarios_supervisor_plantas` (misma política que S1/S9).
// El backend ordena y agrega todo; el frontend solo renderiza y dispara la
// validación por bloque (`PATCH /api/registros/:id/validar`).
import { z } from 'zod';
import { supervisorResumenTurnoSchema } from './supervisor_resumen_hora.js';
/** Un bloque hora×línea×SKU sin validar. */
export const supervisorPendienteBloqueSchema = z.object({
    id: z.string().uuid(),
    /** YYYY-MM-DD (fecha operativa del bloque). */
    fecha: z.string(),
    turno_numero: z.number().int(),
    turno_nombre: z.string(),
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    es_tienda: z.boolean(),
    /** HH:MM */
    hora_inicio: z.string(),
    /** HH:MM */
    hora_fin: z.string(),
    sku_id: z.string().uuid(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
    /** Paquetes. */
    cantidad_procesada: z.number(),
    numero_operarios: z.number().int(),
    /** "Nombre Apellido" del líder (lider_asignado_user_id; si no, capturado_por_user_id). */
    capturado_por_nombre: z.string().nullable(),
    capturado_por_supervisor: z.boolean(),
    numero_op: z.string().nullable(),
    /**
     * true → la OP está terminada (cierre manual o capturada ≥ programada) y este
     * bloque sin validar la retiene fuera del corte de facturación (DN4).
     */
    retiene_facturacion: z.boolean(),
});
/** Estado de una OP referenciada por los bloques listados. */
export const supervisorPendienteOpSchema = z.object({
    numero_op: z.string(),
    cliente_nombre: z.string(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
    /** null si la OP no está en programacion_op. */
    cantidad_programada: z.number().nullable(),
    cantidad_capturada: z.number(),
    /** Bloques sin validar de TODA la OP (no solo los del rango consultado). */
    bloques_pendientes: z.number().int(),
    terminada: z.boolean(),
    cerrada_manual: z.boolean(),
});
export const supervisorPendientesSchema = z.object({
    /** Filtros ya resueltos por el backend (fechas default = día operativo). */
    filtros: z.object({
        desde: z.string(),
        hasta: z.string(),
        turno_numero: z.number().int().nullable(),
        linea_id: z.string().uuid().nullable(),
    }),
    /** Nombres de las plantas del scope (subtítulo). */
    plantas: z.array(z.string()),
    turnos: z.array(supervisorResumenTurnoSchema),
    /** Líneas activas del scope (para el selector). */
    lineas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), es_tienda: z.boolean() })),
    resumen: z.object({
        bloques: z.number().int(),
        paquetes: z.number(),
        /** OPs terminadas con ≥ 1 bloque pendiente. */
        ops_retenidas: z.number().int(),
        /** Fechas distintas con pendientes. */
        dias: z.number().int(),
    }),
    /** Ordenados por fecha, turno, línea, hora. */
    /**
     * Arrastre: TODO lo pendiente de las plantas del supervisor, SIN filtro de
     * fecha. Con el rango en "Hoy" el resumen puede decir "0 OP retenidas" y
     * leerse como que no hay ninguna, cuando el dinero frenado está en meses
     * anteriores. La pantalla lo declara para que no quede escondido.
     */
    arrastre: z.object({
        bloques: z.number().int().min(0),
        paquetes: z.number().min(0),
        ops_retenidas: z.number().int().min(0),
        /** Fecha del bloque pendiente más antiguo (null si no hay). */
        desde: z.string().nullable(),
    }),
    bloques: z.array(supervisorPendienteBloqueSchema),
    /** Solo OPs de los bloques listados; terminadas primero, luego por numero_op. */
    ops: z.array(supervisorPendienteOpSchema),
});
//# sourceMappingURL=supervisor_pendientes.js.map